import { Body } from '@assembly-js/design-system';
import { FormControl } from '@mui/material';
import { createSelector } from '@reduxjs/toolkit';
import { FormikHelpers } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { array, object, string } from 'yup';

import { FormBodyAPI } from 'src/constants';
import { CLIENT_TYPE, COMPANY_TYPE } from 'src/constants/multiSelectConsts';
import { PortalConfigContext, useFeatureFlags } from 'src/context';
import {
  RecipientSelector,
  RecipientType,
} from 'src/features/recipient-selector/RecipientSelector';
import { useAppSelector } from 'src/hooks/useStore';
import {
  ChannelMemberSelect,
  ChannelMemberSelectProps,
} from 'src/legacy/components/MultiSelect/ChannelMemberSelect';
import { ComboBoxOption } from 'src/legacy/components/Select/ComboBox';
import {
  MultiCategorySelect,
  MultiCategorySelectProps,
} from 'src/legacy/components/Select/MultiCategorySelect';
import { RootState } from 'src/store';
import { removeDuplicates } from 'src/utils/array';
import { isUserEnabled } from 'src/utils/UserUtils';

export type ChannelType =
  | 'messaging'
  | 'files'
  | 'fileChannel'
  | 'forms'
  | 'contracts'
  | 'billing'
  | 'extension'
  | 'inbox';

const clientOnlyChannelTypes: ChannelType[] = ['forms', 'contracts'];

const selector = createSelector(
  (state: RootState) => state.clients.isLoading,
  (state: RootState) => state.clients.clients,
  (state: RootState) => state.users.isLoading,
  (isClientsStateLoading, clients, isUsersStateLoading) => ({
    isClientsStateLoading,
    isUsersStateLoading,
    activeClients: clients,
  }),
);

export const useChannelForm = (channelType: ChannelType) => {
  const { t } = useTranslation();
  // when client assignment is enabled only clients can be selected
  // and all channel creates are multi-select
  const isMultiSelect =
    channelType === 'messaging' ||
    channelType === 'forms' ||
    channelType === 'fileChannel' ||
    channelType === 'extension';

  const showClientsInOtherCompanies = channelType === 'forms';
  const filterResultsLimit = channelType === 'forms' ? 10 : 5;

  const baseChannelValidationSchema = object().shape({
    fields: object().shape({
      membersType: string(),
      memberIDs: array()
        .of(string())
        .when('membersType', {
          is: (membersType: string) => {
            return membersType !== 'company';
          },
          then: array().required(
            t(($) => $.validation.channels.select_at_least_one_client),
          ),
        }),
      companyID: string(),
    }),
  });

  const getValidationScheme = () => {
    let validator = Yup.object({});
    switch (channelType) {
      case 'messaging':
        validator = object({
          fields: object().shape({
            name: string(), // keep this for backward compatibility, this not needed and can be removed with EnableManyCompanies
            members: array()
              .of(string())
              .when('membersType', {
                is: (membersType: string) => membersType !== 'company',
                then: array().required(
                  t(($) => $.validation.channels.select_at_least_one_client),
                ),
              }),
            companyID: string(),
          }),
        });
        break;
      case 'forms':
        validator = object().shape({
          fields: object().shape({
            recipientIds: array()
              .of(string())
              .test(
                'clients-array',
                t(($) => $.validation.channels.select_at_least_one_client),
                (value) => value.length > 0,
              ),
            companyID: string(),
          }),
        });
        break;
      case 'fileChannel':
        validator = baseChannelValidationSchema;
        break;
      case 'extension':
        validator = baseChannelValidationSchema;
        break;
      default:
        break;
    }

    return validator;
  };

  const validationScheme = getValidationScheme();

  /**
   * text to describe the form based on the channel type
   */
  const renderInstructionText = () => {
    let text = '';
    switch (channelType) {
      case 'messaging':
        text = `Create a channel where all recipients can see each other.`;
        break;
      case 'fileChannel':
        text = 'Create a channel where all recipients can see uploaded files.';
        break;
      case 'extension':
        text = 'Create a channel where all members can see the app.';
        break;
      default:
        break;
    }
    if (text) {
      return (
        <Body size="sm" className="mb-3">
          {text}
        </Body>
      );
    }
    return null;
  };

  const FormRenderer: React.FC<FormBodyAPI & FormikHelpers<any>> = ({
    values,
    setFieldValue,
    errors,
    touched,
  }) => {
    const portalConfig = React.useContext(PortalConfigContext);
    const { EnableManyCompanies } = useFeatureFlags();

    const { isClientsStateLoading, isUsersStateLoading, activeClients } =
      useAppSelector(selector);

    /**
     * change the form data based on creation of a message channel with client(s) or company
     * @param options combobox options from the company/client selector - companies or clients
     */
    const handleMessagingMembersChanged = (
      _: any,
      options: ComboBoxOption[],
    ) => {
      // set the membership type based on the type of option selected
      // If a company option is selected, we can set the membership type to company
      // and otherwise the membership type is based on the number of options (clients)
      // selected.
      const { selectedCompanyOptions, selectedClientOptions } = options.reduce<{
        selectedCompanyOptions: ComboBoxOption[];
        selectedClientOptions: ComboBoxOption[];
      }>(
        (acc, opt) => {
          if (opt.type === 'company') {
            acc.selectedCompanyOptions.push(opt);
          } else {
            acc.selectedClientOptions.push(opt);
          }
          return acc;
        },
        { selectedCompanyOptions: [], selectedClientOptions: [] },
      );

      let companyID = '';
      let membershipType = 'group';
      if (
        selectedCompanyOptions.length > 0 &&
        selectedClientOptions.length === 0
      ) {
        companyID = selectedCompanyOptions.at(0)?.id ?? '';
        membershipType = 'company';
      } else if (options.length === 1) {
        membershipType = 'individual';
      }

      // even for other types of channels, we need to set the companyID
      // and if the option is not a company, we need to find the first
      // option (client) with a companyId. The selector already handles
      // filters for clients within the same company.
      if (!companyID) {
        const optionWithCompanyId = options.find((opt) => !!opt.companyId);
        if (optionWithCompanyId && optionWithCompanyId.companyId) {
          companyID = optionWithCompanyId.companyId;
        }
      }
      const channelMembers: string[] =
        membershipType === 'company'
          ? [companyID]
          : options
              .filter((opt) => opt.type === CLIENT_TYPE)
              .map((opt) => opt.id);
      const channelGetstreamMembers =
        membershipType === 'company'
          ? [companyID]
          : options
              .filter((opt) => opt.type === CLIENT_TYPE)
              .map((opt) => opt.getstreamId)
              .filter(Boolean);
      const channelTitle =
        membershipType === 'company'
          ? companyID
          : options.map((opt) => opt.label).join(', ');
      setFieldValue('fields.members', channelMembers);
      setFieldValue('fields.getstreamMembers', channelGetstreamMembers);
      setFieldValue('fields.companyID', companyID);
      setFieldValue('fields.name', channelTitle);
      setFieldValue('fields.membersType', membershipType);
    };

    /**
     *
     * @param opts combobox options from the company/client selector - companies or clients
     */
    const handleChannelMembersChanged = (_: any, opts: ComboBoxOption[]) => {
      setFieldValue(
        'fields.memberIDs',
        opts.map((opt: ComboBoxOption) => opt.id),
      );
      if (opts.length > 0) {
        // value types will always be homogeneous so we can simply take the .type of the first selected option
        // when multiple items are selected this must be a group type. Only multiple clients can be selected, company
        // selection does not support multiple items.
        const membershipType = opts.length > 1 ? 'group' : opts.at(0)?.type;
        setFieldValue('fields.membersType', membershipType);
      }

      const optionWithCompanyId = opts.find((opt) => !!opt.companyId);
      if (optionWithCompanyId && optionWithCompanyId.companyId) {
        setFieldValue('fields.companyID', optionWithCompanyId.companyId);
      }
    };

    const handleMembersChanged: MultiCategorySelectProps['onChange'] = (
      _,
      vals,
    ) => {
      if (!Array.isArray(vals)) return;

      if (channelType === 'extension') {
        const channelMembers = vals
          .filter((v) => v.type === CLIENT_TYPE)
          .map((v) => v.id);
        const companyIds = vals
          .filter((v) => v.type === COMPANY_TYPE)
          .map((v) => v.id);

        // get all clients that belong to selected companies
        const companiesAssociatedMembers = removeDuplicates(
          companyIds
            .map((cid) =>
              activeClients
                .filter((c) => {
                  if (EnableManyCompanies) {
                    return (
                      c.fields.companyIds?.includes(cid) && isUserEnabled(c)
                    );
                  } else {
                    return c.fields.companyId === cid && isUserEnabled(c);
                  }
                })
                .map((c) => `${portalConfig.portalHeader}_${c.id}`),
            )
            .flat(),
        );
        const channelMembersNames = vals.map((v) => v.label).join(', ');
        setFieldValue(
          'fields.companiesAssociatedMembers',
          companiesAssociatedMembers,
        );
        setFieldValue('fields.clientUserIds', channelMembers);
        setFieldValue('fields.name', channelMembersNames);
      }

      if (channelType === 'forms') {
        const membersIds = vals.map((val) => val.id);
        setFieldValue('fields.recipientIds', membersIds);
      }
    };

    const getValues = () => {
      let formValue: string | string[] = '';
      switch (channelType) {
        case 'messaging':
          formValue = [].concat(values.fields.members);
          break;
        case 'forms':
          formValue = values.fields.recipientIds || [];
          break;
        case 'fileChannel':
        case 'extension':
          formValue = values.fields.clientUserIds || [];
          break;
        default:
          break;
      }
      return formValue;
    };

    const renderMembersDropdown = () => {
      // if the clients or users are still loading, don't render the dropdown
      if (isClientsStateLoading || isUsersStateLoading) {
        return null;
      }

      let channelMemberProps:
        | Omit<ChannelMemberSelectProps, 'allowMultipleCompanies'>
        | undefined;
      const placeholder = EnableManyCompanies
        ? t(($) => $.page_channels.select_clients_or_company)
        : t(($) => $.page_channels.search_client_or_company);
      if (channelType === 'messaging') {
        channelMemberProps = {
          memberIds: values.fields.members || [],
          onChange: handleMessagingMembersChanged,
          id: 'message-channel-multi-selector',
          label: t(($) => $.page_channels.select_clients_or_company),
          placeholder: placeholder,
          error: Boolean(errors?.fields?.members),
          helperText: errors?.fields?.members || '',
        };
      }
      if (channelType === 'fileChannel') {
        channelMemberProps = {
          memberIds: values.fields.memberIDs || [],
          onChange: handleChannelMembersChanged,
          id: 'file-channel-multi-selector',
          label: t(($) => $.page_channels.select_clients_or_company),
          placeholder: placeholder,
          error: Boolean(errors?.fields?.memberIDs),
          helperText: errors?.fields?.memberIDs || '',
        };
      }
      if (channelType === 'extension') {
        channelMemberProps = {
          memberIds: values.fields.memberIDs || [],
          onChange: handleChannelMembersChanged,
          id: 'extension-multi-selector',
          placeholder: placeholder,
          error: Boolean(errors?.fields?.memberIDs),
          helperText: errors?.fields?.memberIDs || '',
        };
      }

      if (EnableManyCompanies) {
        const recipientTypes: RecipientType[] = ['client'];
        const disableCompanies =
          portalConfig.features.disableCompanies ||
          clientOnlyChannelTypes.includes(channelType);
        if (!disableCompanies) {
          recipientTypes.push('company');
        }
        return (
          <RecipientSelector
            recipientTypes={recipientTypes}
            onRecipientSelected={(selectedRecipients) => {
              const fieldValueMap: Record<ChannelType, string> = {
                forms: 'recipientIds',
                contracts: 'recipientIds',
                billing: 'recipientIds',
                inbox: 'recipientIds',
                files: 'recipientIds',
                messaging: 'members',
                fileChannel: 'memberIDs',
                extension: 'memberIDs',
              };
              if (selectedRecipients.length === 0) {
                setFieldValue(`fields.${fieldValueMap[channelType]}`, []);
                setFieldValue('fields.membersType', '');
                setFieldValue('fields.companyID', '');
                return;
              }
              const firstRecipient = selectedRecipients.at(0);
              if (firstRecipient?.object === 'company') {
                setFieldValue('fields.companyID', firstRecipient.id);
                setFieldValue('fields.membersType', 'company');
                return;
              }
              if (firstRecipient?.object === 'client') {
                const clientIDs = selectedRecipients.map((r) => r.id);
                if (clientIDs.length > 1) {
                  setFieldValue('fields.membersType', 'group');
                } else {
                  setFieldValue('fields.membersType', 'individual');
                }

                setFieldValue('fields.companyID', firstRecipient.companyId);
                setFieldValue('fields.clientUserIds', [firstRecipient.id]);
                setFieldValue(
                  `fields.${fieldValueMap[channelType]}`,
                  clientIDs,
                );

                if (channelType === 'messaging') {
                  const clientMemberIds: string[] = [];
                  selectedRecipients.forEach((val) => {
                    if (val.object === 'client') {
                      clientMemberIds.push(
                        `${portalConfig.portalHeader}_${val.id}`,
                      );
                    }
                  });
                  setFieldValue('fields.getstreamMembers', clientMemberIds);
                }
              }
            }}
            name="recipient"
            placeholder={channelMemberProps?.placeholder}
            error={
              Boolean(
                errors?.fields?.memberIDs ||
                  errors?.fields?.recipientIds ||
                  errors?.fields?.members,
              ) && Boolean(touched?.fields)
            }
            helperText={
              touched?.fields
                ? errors?.fields?.memberIDs ||
                  errors?.fields?.recipientIds ||
                  errors?.fields?.members
                : ''
            }
          />
        );
      }

      if (channelMemberProps) {
        return (
          <ChannelMemberSelect
            {...channelMemberProps}
            allowMultipleCompanies={false}
          />
        );
      }
      return (
        <MultiCategorySelect
          textFieldVariant="medium"
          multiple={isMultiSelect}
          includeInternalUsers={false}
          filterResultsLimit={filterResultsLimit}
          values={getValues()}
          id="members-select"
          label={t(($) => $.page_channels.select_clients_or_company)}
          onChange={handleMembersChanged}
          placeholder={t(($) => $.actions.type_to_find_clients)}
          showPlaceholder={
            (channelType === 'messaging' &&
              (values.fields.members || []).length === 0) ||
            ((channelType === 'extension' || channelType === 'fileChannel') &&
              (values.fields.clientUserIds || []).length === 0)
          }
          showClientsInOtherCompanies={showClientsInOtherCompanies}
        />
      );
    };

    return (
      <FormControl fullWidth>
        {renderInstructionText()}
        {renderMembersDropdown()}
      </FormControl>
    );
  };

  return { validationScheme, FormRenderer };
};
